import ApiService from "@/api/ApiService";
import { ImageUploadPicker } from "@/shared/sharedService/ImageUploadPicker";
import {
  CircularProgress,
  Dialog,
  Box,
  DialogContent,
  Stack,
  Typography,
  Button,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { Trash, Eye } from "lucide-react";
import React from "react";
import { useState } from "react";

type FileCategory = {
  id: number;
  type: "project" | "resource";
  label: string;
  description: string;
  defaultCategory: boolean;
};

const initialState = (parentKey, parentId, categoryId: number | null = null) => ({
  id: 0,
  [parentKey]: parentId,
  deleted: false,
  description: "",
  originalFilenames: {},
  filename: "",
  createdAt: dayjs.utc().format("YYYY-MM-DD HH:mm:ss"),
  betterCategory: categoryId,
});

export function FileAdder({ Opener, disabled = false, parentId, parentKey, invalidationId }) {
  const queryClient = useQueryClient();

  const [modalVisible, setModalVisible] = useState(false);
  const [state, setState] = useState(initialState(parentKey, parentId));
  const { data: categories = [], isLoading: categoriesLoading } = useQuery<FileCategory[]>({
    queryKey: ["fileCategories", parentKey],
    queryFn: ApiService.getEntitiesWithFilter("fileCategory", { type: parentKey }),
  });

  const mutation = useMutation({
    mutationFn:
      state.id > 0
        ? ApiService.editEntity(parentKey === "project" ? "projectFile" : "resourceFile", state.id, "app")
        : ApiService.createEntity(parentKey === "project" ? "projectFile" : "resourceFile", { v: "app" }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [parentKey === "project" ? "getProjectJob" : "getDeviceByNumber", "" + invalidationId],
      });
      close();
    },
  });

  const handlePresentModalPress = React.useCallback(() => {
    setModalVisible(true);
  }, []);

  const close = React.useCallback(() => {
    setModalVisible(false);
  }, []);

  const openAndSet = React.useCallback(() => {
    setState(initialState(parentKey, parentId, categories.find((category) => category.defaultCategory)?.id ?? null));
    handlePresentModalPress();
  }, [parentKey, parentId, categories, handlePresentModalPress]);

  const valid = state.betterCategory !== null;

  return (
    <>
      {categoriesLoading ? <CircularProgress size={24} /> : <Opener onPress={disabled ? () => {} : openAndSet} />}

      <Dialog open={modalVisible} onClose={() => setModalVisible(false)} fullWidth maxWidth="sm">
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="h5">Datei hinzufügen</Typography>
              </Box>

              {state.id === 0 ? null : mutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                <Button
                  onClick={() => mutation.mutate({ ...state, deleted: true })}
                  size="small"
                  color="error"
                  variant="contained"
                  startIcon={<Trash />}
                >
                  Löschen
                </Button>
              )}
            </Stack>

            <Typography variant="h6">Datei/Bild</Typography>

            {state.filename.length > 0 ? (
              <Stack spacing={1}>
                {state.filename.split(",").map((fn, i, f) => (
                  <Paper
                    key={fn}
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Typography>
                        Datei {i + 1} von {f.length}
                      </Typography>

                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => window.open(import.meta.env.VITE_APP_URL + "/image/" + fn, "_blank")}>
                          <Eye />
                        </Button>

                        <Button
                          size="small"
                          color="error"
                          variant="contained"
                          onClick={() =>
                            setState((s) => ({
                              ...s,
                              filename: s.filename
                                .split(",")
                                .filter((f) => f !== fn)
                                .join(","),
                            }))
                          }
                        >
                          <Trash />
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            ) : null}

            <ImageUploadPicker
              apiService={ApiService}
              onUploadMultiple={(items) =>
                setState((s) => ({
                  ...s,
                  originalFilenames: {
                    ...s.originalFilenames,
                    ...Object.fromEntries(items.map((x) => [x.file, x.fileName])),
                  },
                  filename: s.filename
                    .split(",")
                    .concat(items.map((x) => x.file))
                    .filter((x) => x.length > 0)
                    .join(","),
                }))
              }
              allowFiles
              allowMultiple
            />

            <Typography variant="h6">Beschreibung</Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={state.description}
              onChange={(e) => {
                const description = e.target.value;
                setState((s) => ({
                  ...s,
                  description,
                }));
              }}
            />

            <Typography variant="h6">Kategorie</Typography>
            <FormControl fullWidth>
              <Select
                value={state.betterCategory ?? ""}
                disabled={categoriesLoading}
                onChange={(e) =>
                  setState((s) => ({
                    ...s,
                    betterCategory: Number(e.target.value),
                  }))
                }
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {mutation.isPending ? (
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Button disabled={!valid} variant="contained" onClick={() => mutation.mutate(state)}>
                Speichern
              </Button>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
